<template>
  <main class="main main--full-height main--connexion">
    <div class="box box--connexion">
      <form class="form" @submit.prevent="signUp">
        <!-- Identifiant -->
        <input
          v-model="identifiant"
          type="text"
          class="input input--text"
          placeholder="Identifiant"
          required
          id="identifiant"
          ref="identifiantRef"
        />

        <!-- Code à 6 chiffres -->
        <PinCodeComponent v-model="code" ref="pinCodeRef" />

        <!-- Conteneur externe -->
        <div class="turnstile-wrapper" ref="turnstileOuter">
          <!-- Conteneur interne -->
          <div ref="turnstileInner">
            <vue-turnstile
              :site-key="turnstileKey"
              v-model="token"
              appearance="interaction-only"
              theme="light"
              class="turnstile-element"
            />
          </div>
        </div>

        <!-- Bouton -->
        <ButtonElement
          type="submit"
          color="bordeaux"
          :icon="PhCashRegister"
          :disabled="!identifiant || !pinCodeRef?.valideCode || token === ''"
        >
          S'enregistrer
        </ButtonElement>

        <!-- Retour -->
        <router-link :to="{ name: 'connexion' }" custom v-slot="{ navigate }">
          <ButtonElement color="bordeaux" bordered :icon="PhArrowLeft" @click="navigate">
            Retour à la connexion
          </ButtonElement>
        </router-link>
      </form>
    </div>
  </main>
</template>

<script setup lang="ts">
/**
 * Gestion de l'enregistrement avec Turnstile + Passkey.
 * Adaptation automatique du widget Cloudflare Turnstile.
 *
 * Le widget charge un iframe de manière asynchrone → sa hauteur réelle
 * n’est connue qu’après montage. On utilise :
 *   - ResizeObserver : détecte la hauteur réelle de l’iframe
 *   - IntersectionObserver : déclenche un premier ajustement dès apparition
 * -> Observes dynamiquement la taille du DOM interne du widget Turnstile
 *   et ajuste le wrapper pour éviter un espace vide si le widget est "absent".
 * -> Si la hauteur interne est < 20px : on masque le wrapper (height: 0)
 *   et on compense le gap par margin-bottom: -1rem.
 */

import '@/assets/css/pages/_inscription.css'
import { PhArrowLeft, PhCashRegister } from '@phosphor-icons/vue'
import ButtonElement from '@/components/ButtonElement.vue'
import { startRegistration } from '@simplewebauthn/browser'
import useFetch from '@/composables/useFetch.ts'
import { nextTick, onMounted, onBeforeUnmount, ref, type Ref, useTemplateRef } from 'vue'
import { useLoadingStore } from '@/stores/loading.ts'
import { useRouter } from 'vue-router'
import VueTurnstile from 'vue-turnstile'
import PinCodeComponent from '@/components/ordre-fabrication/PinCodeComponent.vue'

// Dépendances
const api = useFetch()
const loadingStore = useLoadingStore()
const router = useRouter()

// Données
const identifiant: Ref<string> = ref('')
const code: Ref<string> = ref('')
const token: Ref<string> = ref('')

// Références DOM
const pinCodeRef = useTemplateRef('pinCodeRef')
const identifiantRef = ref<HTMLInputElement | null>(null)
const turnstileOuter = ref<HTMLElement | null>(null) // wrapper externe (gère le margin)
const turnstileInner = ref<HTMLElement | null>(null) // wrapper interne (scaled)

// Environnement
const turnstileKey = import.meta.env.VITE_TURNSTILE_PUBLIC_KEY

// Constantes d’affichage
const BASE_WIDTH = 300 // largeur de référence du widget Turnstile
const MIN_VISIBLE_HEIGHT = 20 // seuil en px en dessous duquel on considère le widget Turnstile "absent"
const GAP_COMPENSATION = '-1rem' // supprime le gap entre inputs quand le widget est invisible

// Observers (sera initialisé dans onMounted)
let resizeObserver: ResizeObserver | null = null
let intersectionObserver: IntersectionObserver | null = null

/**
 * Ajuste la taille du wrapper en fonction de la taille réelle du widget Turnstile.
 *
 * Comportement :
 * - Si hauteur < MIN_VISIBLE_HEIGHT : on masque complètement le wrapper (height: 0) et supprime le gap lié
 * - Sinon : on calcule un scale en proportionnel à la largeur disponible et ajuste la hauteur en conséquence
 */
function updateTurnstileAppearance(height: number, inner: HTMLElement) {
  const outer = turnstileOuter.value
  if (!outer) return

  // Cas 1 : Si le Turnstile n’est pas encore chargé ou invisible → on masque
  if (height < MIN_VISIBLE_HEIGHT) {
    inner.style.height = '0px'
    inner.style.transform = 'scale(0)'
    inner.style.transformOrigin = 'top left'
    outer.style.marginBottom = GAP_COMPENSATION
    return
  }

  // Cas 2 : Turnstile visible → on ajuste normalement
  const availableWidth = inner.clientWidth
  const scale = Math.min(1, availableWidth / BASE_WIDTH)

  inner.style.height = `${height * scale}px`
  inner.style.transform = `scale(${scale})`
  inner.style.transformOrigin = 'top left'
  outer.style.marginBottom = '' // remet le margin normal
}

/**
 * Initialise le ResizeObserver pour réagir :
 *   - à l’apparition de l’iframe interne du Turnstile
 *   - à tout changement de hauteur du widget
 */
function setupResizeObserver(widgetEl: HTMLElement, inner: HTMLElement) {
  resizeObserver = new ResizeObserver((entries) => {
    for (const entry of entries) {
      updateTurnstileAppearance(entry.contentRect.height, inner)
    }
  })
  resizeObserver.observe(widgetEl)
}

/**
 * Initialise l’IntersectionObserver pour déclencher une première mesure
 * dès que le widget devient visible dans la fenêtre.
 */
function setupIntersectionObserver(widgetEl: HTMLElement, inner: HTMLElement) {
  intersectionObserver = new IntersectionObserver(
    (entries) => {
      if (entries.some((e) => e.isIntersecting)) {
        updateTurnstileAppearance(widgetEl.offsetHeight, inner)
      }
    },
    { threshold: 0.01 },
  )
  intersectionObserver.observe(widgetEl)
}

onMounted(() => {
  // Met automatiquement le focus sur l'input identifiant
  nextTick(() => {
    identifiantRef.value?.focus()
  })

  nextTick(() => {
    const inner = turnstileInner.value
    if (!inner) return

    // Sélectionne l’élément du widget Turnstile
    const widgetEl = inner.querySelector('.turnstile-element') as HTMLElement | null
    if (!widgetEl) return

    // Mise en place des deux observers
    setupIntersectionObserver(widgetEl, inner)
    setupResizeObserver(widgetEl, inner)

    // Ajoute un recalcul lors du resize fenêtre
    window.addEventListener('resize', () => updateTurnstileAppearance(widgetEl.offsetHeight, inner))
  })
})

onBeforeUnmount(() => {
  resizeObserver?.disconnect()
  intersectionObserver?.disconnect()
  resizeObserver = null
  intersectionObserver = null
})

/**
 * Traitement complet de l’inscription :
 * - Vérification Turnstile
 * - Options Passkey (WebAuthn)
 * - Enregistrement final
 */
async function signUp() {
  loadingStore.start()
  try {
    const options = await api.post(
      'auth/inscription/options',
      {
        identifiant: identifiant.value,
        code: code.value,
        token: token.value,
      },
      true,
      undefined,
    )
    const credential = await startRegistration({ optionsJSON: options })
    await api.post('auth/inscription', credential)
    loadingStore.stop()
    await router.push({ name: 'accueil' })
  } finally {
    loadingStore.stop()
  }
}
</script>
